import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Eye, Globe, Lock, Mail, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Privacy() {
  return <Layout data-id="element-5172">
      <div className="container mx-auto px-4 py-12" data-id="element-5173">
        {/* Hero */}
        <div className="mb-12" data-id="element-5174">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-500/10 text-teal-500 text-xs font-medium mb-4 border border-teal-500/20" data-id="element-5175">
            <Eye className="h-3 w-3 mr-2" data-id="element-5176" />
            Tools Library
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-5177">
            Privacy Tools
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-5178">
            Take back control of your data. Use these tools to protect your
            online activity from surveillance, tracking, and data brokers.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-5179">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-5180">
            {/* Why This Matters */}
            <section data-id="element-5181">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-5182">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-teal-500 p-6 rounded-r-lg" data-id="element-5183">
                <p className="text-slate-300 mb-4" data-id="element-5184">
                  Privacy is a fundamental right, but mass surveillance and data
                  tracking are pervasive. Governments, corporations, and
                  criminals build detailed profiles of your life based on your
                  digital footprint.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-5185">
                  <li className="flex items-start" data-id="element-5186">
                    <span className="text-teal-500 mr-2" data-id="element-5187">•</span> Data brokers
                    sell your personal information without your consent
                  </li>
                  <li className="flex items-start" data-id="element-5188">
                    <span className="text-teal-500 mr-2" data-id="element-5189">•</span> ISPs can see
                    every website you visit (unless you use a VPN/DoH)
                  </li>
                  <li className="flex items-start" data-id="element-5190">
                    <span className="text-teal-500 mr-2" data-id="element-5191">•</span> Privacy tools
                    help you minimize your digital footprint
                  </li>
                </ul>
              </div>
            </section>

            {/* Tool Categories */}
            <section data-id="element-5192">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5193">
                Recommended Tools
              </h2>
              <div className="grid gap-6" data-id="element-5194">
                <ToolCategory title="Encrypted Messaging" icon={Lock} desc="Communicate without eavesdropping. Only you and the recipient can read messages." tools={[{
                name: 'Signal',
                note: 'Gold standard, open source'
              }, {
                name: 'Threema',
                note: 'Swiss-based, no phone number needed'
              }, {
                name: 'WhatsApp',
                note: 'E2E encrypted, but Meta-owned'
              }]} safety="Verify safety numbers for high-stakes conversations." data-id="element-5195" />
                <ToolCategory title="VPN Services" icon={Globe} desc="Encrypt your internet traffic and hide your IP address." tools={[{
                name: 'Mullvad',
                note: 'No logs, anonymous payment'
              }, {
                name: 'ProtonVPN',
                note: 'Swiss-based, free tier available'
              }, {
                name: 'IVPN',
                note: 'Privacy-focused, audit logs'
              }]} safety="Avoid free VPNs—they often sell your data to cover costs." data-id="element-5196" />
                <ToolCategory title="Secure DNS" icon={Shield} desc="Prevent your ISP from seeing which websites you visit." tools={[{
                name: 'Cloudflare (1.1.1.1)',
                note: 'Fast, privacy-focused'
              }, {
                name: 'Quad9 (9.9.9.9)',
                note: 'Blocks malicious domains'
              }, {
                name: 'NextDNS',
                note: 'Customizable blocking'
              }]} safety="Configure at the router level to protect all devices on your network." data-id="element-5197" />
                <ToolCategory title="Browser Extensions" icon={Eye} desc="Block ads, trackers, and malicious scripts." tools={[{
                name: 'uBlock Origin',
                note: 'Best ad/tracker blocker'
              }, {
                name: 'Privacy Badger',
                note: 'Learns to block trackers'
              }, {
                name: 'HTTPS Everywhere',
                note: 'Forces secure connections'
              }]} safety="Only install extensions from official stores and developers." data-id="element-5198" />
              </div>
            </section>

            {/* Implementation Guide */}
            <section data-id="element-5199">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5200">
                Implementation Guide
              </h2>
              <div className="space-y-4" data-id="element-5201">
                <Step number={1} text="Install Signal and migrate sensitive conversations." data-id="element-5202" />
                <Step number={2} text="Configure secure DNS (1.1.1.1) on your devices." data-id="element-5203" />
                <Step number={3} text="Install uBlock Origin on your browser." data-id="element-5204" />
                <Step number={4} text="Set up a reputable VPN for use on public Wi-Fi." data-id="element-5205" />
                <Step number={5} text="Consider switching to a privacy-focused email provider like ProtonMail." data-id="element-5206" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-5207">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5208">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-5209">
                <ul className="space-y-3 text-slate-300" data-id="element-5210">
                  <li className="flex items-start" data-id="element-5211">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5212" />
                    Using free VPNs that monetize your data.
                  </li>
                  <li className="flex items-start" data-id="element-5213">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5214" />
                    Installing too many browser extensions (increases attack
                    surface).
                  </li>
                  <li className="flex items-start" data-id="element-5215">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5216" />
                    Trusting "incognito mode" to protect you from ISP tracking
                    (it doesn't).
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-5217">
            <Card className="p-6 sticky top-24" data-id="element-5218">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-5219">
                Quick Actions
              </h3>
              <Link to="/personal/internet" className="block w-full" data-id="element-5220">
                <Button className="w-full bg-teal-500 hover:bg-teal-600 text-slate-950" data-id="element-5221">
                  Internet Safety Guide <ArrowRight className="ml-2 h-4 w-4" data-id="element-5222" />
                </Button>
              </Link>
              <Link to="/tools/breach-check" className="block w-full mt-3" data-id="element-5223">
                <Button variant="secondary" className="w-full" data-id="element-5224">
                  Check for Breaches
                </Button>
              </Link>

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-5225">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-5226">
                  Related
                </h4>
                <ul className="space-y-2 text-sm text-slate-400" data-id="element-5227">
                  <li data-id="element-5228">
                    <Link to="/personal/communication" className="hover:text-teal-500" data-id="element-5229">
                      Secure Communication
                    </Link>
                  </li>
                  <li data-id="element-5230">
                    <Link to="/personal/data-backup" className="hover:text-teal-500" data-id="element-5231">
                      Data Protection
                    </Link>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ToolCategory({
  title,
  icon: Icon,
  desc,
  tools,
  safety
}: {
  title: string;
  icon: any;
  desc: string;
  tools: {
    name: string;
    note: string;
  }[];
  safety: string;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800" data-id="element-5232">
      <div className="flex items-start mb-4" data-id="element-5233">
        <div className="bg-teal-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-5234">
          <Icon className="h-6 w-6 text-teal-500" data-id="element-5235" />
        </div>
        <div data-id="element-5236">
          <h3 className="text-lg font-bold text-slate-200 mb-1" data-id="element-5237">{title}</h3>
          <p className="text-slate-400 text-sm" data-id="element-5238">{desc}</p>
        </div>
      </div>

      <div className="space-y-3 mb-6 pl-16" data-id="element-5239">
        {tools.map((tool, idx) => <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 last:border-0 last:pb-0" data-id="element-5240">
            <span className="font-bold text-slate-300" data-id="element-5241">{tool.name}</span>
            <span className="text-slate-500" data-id="element-5242">{tool.note}</span>
          </div>)}
      </div>

      <div className="bg-slate-800/50 p-3 rounded text-xs text-slate-400 flex items-start" data-id="element-5243">
        <Shield className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" data-id="element-5244" />
        <span data-id="element-5245">
          <strong data-id="element-5246">Safety Note:</strong> {safety}
        </span>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-5247">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-teal-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-5248">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-5249">{text}</p>
    </div>;
}